<template>
    <VJoyWrapper align="flex-start" direction="column" no-margin row-gap="2">
        <AutoComplete
            ref="autocompleteRef"
            v-model="industryExpertiseInput"
            :data-testid="`${dataTestid}-autocomplete`"
            :disabled="disabled"
            :empty-search-message="t('generic.no.results.found')"
            :placeholder="placeholder ?? t('profile.section.industry.expertises.edit.placeholder')"
            :suggestions="suggestions"
            complete-on-focus
            option-label="label"
            scroll-height="40vh"
            @click="handleClick"
            @item-select="addItem"
        />
        <VJoyFormError v-for="error of errors" :key="error" no-margin>
            {{ error }}
        </VJoyFormError>
        <VJoyWrapper v-if="!single" ref="tagsListRef" :data-testid="`${dataTestid}-list`" column-gap="2" no-margin row-gap="2">
            <VJoyTag
                v-for="(item, index) in model"
                :key="item.humanId"
                :data-testid="`${dataTestid}-tag-${index}`"
                removable
                @remove="removeItem(item.humanId)"
            >
                {{ item.label }}
            </VJoyTag>
        </VJoyWrapper>
    </VJoyWrapper>
</template>

<script lang="ts" setup>
    import {computed, nextTick, onMounted, ref, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import AutoComplete, {type AutoCompleteOptionSelectEvent} from 'primevue/autocomplete';
    import {useTranslation} from '#imports';
    import {VJoyFormError, VJoyTag, VJoyWrapper} from '@maltjoy/core-vue';
    import type {IndustryExpertiseEditionItem} from '~/features/about/types/EditAboutSection';
    import {useIndustryExpertiseStore} from '~/features/industry-expertise/store/industryExpertiseStore';

    interface Props {
        placeholder?: string;
        dataTestid?: string;
        disabled?: boolean;
        errors?: string[];
        single?: boolean;
    }
    const {placeholder, dataTestid = 'industry-expertises-autocomplete', disabled = false, errors = [], single = false} = defineProps<Props>();
    const model = defineModel<IndustryExpertiseEditionItem[]>({required: true});

    const {t} = useTranslation();
    const {industryExpertises} = storeToRefs(useIndustryExpertiseStore());
    const {loadIndustryExpertises} = useIndustryExpertiseStore();

    const autocompleteRef = useTemplateRef<InstanceType<typeof AutoComplete>>('autocompleteRef');
    const tagsListRef = useTemplateRef<InstanceType<typeof VJoyWrapper>>('tagsListRef');
    const industryExpertiseInput = ref<string>(single ? (model.value[0]?.label ?? '') : '');

    const suggestions = computed(() =>
        industryExpertises.value
            .filter((item) => !model.value.some((selected) => selected.humanId === item.humanId))
            .filter(
                (item) =>
                    (industryExpertiseInput.value?.length ?? 0) < 1 ||
                    (industryExpertiseInput.value && item.label.toLowerCase().includes(industryExpertiseInput.value.toLowerCase())),
            ),
    );

    function addItem(event: AutoCompleteOptionSelectEvent) {
        const selected = event.value as IndustryExpertiseEditionItem;
        if (single) {
            model.value = [selected];
            nextTick(() => {
                industryExpertiseInput.value = selected.label ?? '';
            });
        } else {
            model.value = [...model.value, selected];
            nextTick(() => {
                industryExpertiseInput.value = '';
                const el = tagsListRef.value?.$el as HTMLElement | undefined;
                el?.lastElementChild?.scrollIntoView({block: 'nearest'});
            });
        }
    }

    function handleClick() {
        if (single && industryExpertiseInput.value) {
            industryExpertiseInput.value = '';
            nextTick(() => {
                (autocompleteRef.value as unknown as {show?: () => void})?.show?.();
            });
        }
    }

    function removeItem(humanId: string) {
        model.value = model.value.filter((item) => item.humanId !== humanId);
    }

    onMounted(async () => {
        await loadIndustryExpertises();
    });
</script>
